import React, { useEffect, useState } from "react"
import { Vertical } from "@/components/standard/BFLayout"
import BFTaggedArrayInput from "@/components/standard/BFTaggedArrayInput"
import ZipCodeSelector from "@/components/properties/components/ZipCodeSelector"
import { H5, Small } from "@/components/standard/BFText"

interface BuyBoxZipCodeInputProps {
  zipCodes: string[]
  onZipCodesChange: (updatedZipCodes: string[]) => void
  onSuggestionsChange?: () => void // Optional callback for suggestions
}

const BuyBoxZipCodeInput = ({
  zipCodes,
  onZipCodesChange,
  onSuggestionsChange,
}: BuyBoxZipCodeInputProps) => {
  const hasTags = zipCodes.length > 0
  const [localZipCodes, setLocalZipCodes] = useState<string[]>(zipCodes)
  const [zipCodeInput, setZipCodeInput] = useState("")
  const [suggestions, setSuggestions] = useState<string[]>([]) // Track suggestions from ZipCodeSelector

  useEffect(() => {
    setLocalZipCodes(zipCodes) // Sync prop state
  }, [zipCodes])

  useEffect(() => {
    // Trigger callback only when `suggestions` state changes
    if (onSuggestionsChange && suggestions.length > 0) {
      onSuggestionsChange()
    }
  }, [suggestions, onSuggestionsChange])

  // Add ZIP Code
  const addZipCode = (zip: string) => {
    if (!localZipCodes.includes(zip)) {
      const updatedZipCodes = [...localZipCodes, zip]
      setLocalZipCodes(updatedZipCodes)
      onZipCodesChange(updatedZipCodes)
      setZipCodeInput("") // Clear input
    }
  }

  // Remove ZIP Code
  const removeZipCode = (zip: string) => {
    const updatedZipCodes = localZipCodes.filter((z) => z !== zip)
    setLocalZipCodes(updatedZipCodes)
    onZipCodesChange(updatedZipCodes)
  }

  // Trigger onDone to grab the first suggestion
  const handleDone = () => {
    if (suggestions.length > 0) {
      addZipCode(suggestions[0]) // Add the first suggestion
    }
  }

  return (
    <Vertical className="pt-5">
      <H5 className="text-midnight-blue">ZIP Code</H5>
      <Small className="text-midnight-blue mb-1">Enter one or more</Small>
      <BFTaggedArrayInput
        tags={localZipCodes}
        onDone={handleDone}
        onRemoveTag={removeZipCode}
        placeholder={hasTags ? "Add another ZIP Code " : "ZIP Code"}
        value={zipCodeInput}
        onChangeText={setZipCodeInput}
      />

      <ZipCodeSelector
        input={zipCodeInput}
        onSelect={addZipCode}
        onSuggestionsUpdate={setSuggestions} // Update parent suggestions
      />
    </Vertical>
  )
}

export default BuyBoxZipCodeInput
